/**
 * The small parts Customer 360's seven tabs are built from (D19 phase 21b).
 *
 * The kit allows seven record tabs; we had seventeen. Each old tab is now a VIEW inside the tab that holds it, chosen
 * with a small segmented control whose choice lives in the URL (`?view=`). An old link (`?tab=payments`) still lands
 * on its content: the page maps the old tab id to its new tab (tabAliases) and the view switch takes the old id as
 * its view when no `view` is given.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronRight, MoreHorizontal } from 'lucide-react';
import { useSession } from '../../lib/session';
import { Tabs } from '../../ui/navigation';
import { EmptyState, cx } from '../../ui/primitives';

export interface CustomerView {
  id: string;
  label: string;
  permission?: string;
  render: () => ReactNode;
}

/** Opens a tab (and a view inside it) of this record, keeping every other parameter. */
export function useOpenTab() {
  const [params, setParams] = useSearchParams();
  return (tab: string, view?: string) => {
    const copy = new URLSearchParams(params);
    copy.set('tab', tab);
    if (view) copy.set('view', view); else copy.delete('view');
    setParams(copy, { replace: true });
  };
}

export function ViewSwitch({ views, label, bare = false }: { views: readonly CustomerView[]; label: string; bare?: boolean }) {
  const { can } = useSession();
  const [params, setParams] = useSearchParams();
  const visible = views.filter((v) => !v.permission || can(v.permission));
  if (visible.length === 0) {
    return <EmptyState title="Nothing here you may see" detail="Each part of this tab needs its own permission; you hold none of them." />;
  }
  const wanted = params.get('view') ?? params.get('tab');
  const active = visible.find((v) => v.id === wanted) ?? visible[0]!;
  return (
    <div className={cx(bare && 'grid gap-4')}>
      {visible.length > 1 ? (
        <div aria-label={label} className={cx(!bare && 'border-b border-line px-3 py-1.5', bare && '-mt-1')}>
          <Tabs
            variant="sub"
            tabs={visible.map((v) => ({ id: v.id, label: v.label }))}
            active={active.id}
            onChange={(next) => { const copy = new URLSearchParams(params); copy.set('view', next); setParams(copy, { replace: true }); }}
          />
        </div>
      ) : null}
      <div key={active.id}>{active.render()}</div>
    </div>
  );
}

/** A card of the Overview: a title, an optional note at the right, and one link to the tab that holds the whole story. */
export function OverviewCard({ title, note, link, children, className }: {
  title: ReactNode; note?: ReactNode; link?: { label: string; onClick: () => void }; children: ReactNode; className?: string;
}) {
  return (
    <section className={cx('panel rounded-[var(--radius-panel)] border border-line bg-surface', className)}>
      <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-2.5">
        <h2 className="truncate text-section">{title}</h2>
        {note ? <span className="truncate text-small text-ink-muted">{note}</span> : null}
      </header>
      <div className="px-4 py-3">{children}</div>
      {link ? (
        <footer className="border-t border-line px-4 py-2">
          <button type="button" onClick={link.onClick} className="inline-flex items-center gap-1 text-small font-medium text-brand hover:underline">
            {link.label}<ChevronRight size={12} aria-hidden />
          </button>
        </footer>
      ) : null}
    </section>
  );
}

/** "More actions": a short menu of buttons. Escape or a click elsewhere closes it. */
export function MoreMenu({ items }: { items: readonly ReactNode[] }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const id = useId();
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('mousedown', away);
    window.addEventListener('keydown', key);
    return () => { window.removeEventListener('mousedown', away); window.removeEventListener('keydown', key); };
  }, [open]);
  return (
    <div ref={box} className="relative">
      <button type="button" aria-haspopup="true" aria-expanded={open} aria-controls={id} aria-label="More actions" onClick={() => setOpen(!open)}
        className="btn btn-secondary inline-flex h-[30px] items-center gap-1.5 rounded-[var(--radius-control)] border border-line-strong bg-surface px-2.5 text-small font-medium text-ink hover:bg-surface-sunken">
        <MoreHorizontal size={14} aria-hidden />More
      </button>
      {open ? (
        <div id={id} className="overlay-surface absolute left-0 z-30 mt-1 grid min-w-[12rem] gap-1 rounded-[var(--radius-control)] border border-line bg-surface p-1.5 shadow-[var(--shadow-overlay)]"
          onClick={() => setOpen(false)}>
          {items}
        </div>
      ) : null}
    </div>
  );
}
